<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

/**
 * 交付物挂文件的配对工作台。
 *
 * 一个项目 123 个交付物、157 个真实文件，逐个打开抽屉选文件要点上千次。
 * 而「按名称自动匹配」这条路已经验证不通：交付物名是活动名（编制模具清单），
 * 文件名是产出物名（D11项目 检具加工进度表），122/123 的相似度低于 0.25。
 *
 * 所以不猜，只把两边摆整齐让人点：**点左边一个交付物 → 点右边一个文件 → 立刻挂上**，
 * 两条同时从清单里消失，接着配下一对。手不用离开这一屏。
 */
const props = defineProps<{ projectId: string | number }>();
const emit = defineEmits<{ paired: [] }>();

interface Deliverable {
  id: number; title: string; code: string | null;
  nodeName: string | null; phase: string | null; ownerName: string | null;
}
interface FileItem {
  id: number; title: string; code: string | null;
  sizeBytes: number; versionCount: number; updatedAt: string | null;
}

const deliverables = ref<Deliverable[]>([]);
const files = ref<FileItem[]>([]);
const placeholderCount = ref(0);
const loading = ref(true);
const error = ref('');
const pairing = ref(false);
const pickedDeliverable = ref<Deliverable | null>(null);
const deliverableQuery = ref('');
const fileQuery = ref('');
const pairedCount = ref(0);

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.deliverablePairing(props.projectId);
    deliverables.value = (payload.deliverables ?? []) as unknown as Deliverable[];
    files.value = (payload.files ?? []) as unknown as FileItem[];
    placeholderCount.value = Number(payload.placeholderCount ?? 0);
  } catch (caught) {
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

onMounted(load);

function matches(text: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return !needle || text.toLowerCase().includes(needle);
}

const visibleDeliverables = computed(() => deliverables.value.filter(
  (item) => matches(`${item.title} ${item.code ?? ''} ${item.phase ?? ''} ${item.nodeName ?? ''}`, deliverableQuery.value),
));
const visibleFiles = computed(() => files.value.filter(
  (item) => matches(`${item.title} ${item.code ?? ''}`, fileQuery.value),
));

/** 点文件即挂接；没先选交付物时提示先选左边 */
async function pairWith(file: FileItem): Promise<void> {
  const target = pickedDeliverable.value;
  if (!target) {
    notify('error', '先选左边的交付物', '先点一个还没挂文件的交付物，再点右边要挂给它的文件。');
    return;
  }
  pairing.value = true;
  try {
    await eqsApi.updateProjectDeliverable(props.projectId, target.id, { documentId: file.id });
    // 两边同时移出清单，接着配下一对——不重新拉整份数据，手感不断
    deliverables.value = deliverables.value.filter((item) => item.id !== target.id);
    files.value = files.value.filter((item) => item.id !== file.id);
    pickedDeliverable.value = null;
    pairedCount.value += 1;
    emit('paired');
  } catch (caught) {
    notify('error', '挂接失败', errorMessage(caught));
  } finally {
    pairing.value = false;
  }
}

function sizeLabel(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}
</script>

<template>
  <section class="pairing">
    <header class="pairing-head">
      <div>
        <span>PAIR UP</span>
        <h3>批量挂交付物文件</h3>
        <p>
          点左边一个交付物，再点右边要挂给它的文件，立刻生效。
          右边只列**有实际内容**的文件——按名称自动匹配试过了，交付物名是活动名、
          文件名是产出物名，对不上，只有人知道哪个是哪个。
        </p>
      </div>
      <b v-if="pairedCount" class="pairing-done">本次已挂 {{ pairedCount }} 个</b>
    </header>

    <p v-if="loading" class="pairing-hint">正在读取…</p>
    <p v-else-if="error" class="pairing-hint pairing-error">读取失败：{{ error }}</p>

    <template v-else>
      <p v-if="placeholderCount" class="pairing-note">
        另有 {{ placeholderCount }} 条 0 字节的空文件记录（迁移时按活动名建的空壳），
        已从右边隐藏——挂上去只会让缺口看起来补上了，实际没有任何证据。
      </p>

      <div class="pairing-grid">
        <div class="pairing-col">
          <header>
            <b>没挂文件的交付物</b><small>{{ visibleDeliverables.length }} / {{ deliverables.length }}</small>
          </header>
          <input v-model="deliverableQuery" type="search" placeholder="按名称、阶段筛选">
          <ul v-if="visibleDeliverables.length">
            <li v-for="item in visibleDeliverables" :key="item.id">
              <button
                type="button"
                :class="{ picked: pickedDeliverable?.id === item.id }"
                @click="pickedDeliverable = pickedDeliverable?.id === item.id ? null : item"
              >
                <b>{{ item.title }}</b>
                <small>
                  <template v-if="item.phase">{{ item.phase }} · </template>{{ item.nodeName || '未挂节点' }}
                  <template v-if="item.ownerName"> · {{ item.ownerName }}</template>
                </small>
              </button>
            </li>
          </ul>
          <p v-else class="pairing-empty">
            {{ deliverables.length ? '当前筛选没有结果。' : '这个项目的交付物都挂上文件了。' }}
          </p>
        </div>

        <div class="pairing-col">
          <header>
            <b>还没被认领的文件</b><small>{{ visibleFiles.length }} / {{ files.length }}</small>
          </header>
          <input v-model="fileQuery" type="search" placeholder="按文件名筛选">
          <p v-if="pickedDeliverable" class="pairing-target">
            要挂给：<b>{{ pickedDeliverable.title }}</b> —— 点下面的文件即可
          </p>
          <p v-else class="pairing-target pairing-target-idle">先在左边点一个交付物</p>
          <ul v-if="visibleFiles.length">
            <li v-for="item in visibleFiles" :key="item.id">
              <button type="button" :disabled="pairing || !pickedDeliverable" @click="pairWith(item)">
                <b>{{ item.title }}</b>
                <small>
                  {{ sizeLabel(item.sizeBytes) }} · {{ item.versionCount }} 个版本
                  <template v-if="item.updatedAt"> · {{ item.updatedAt.slice(0, 10) }}</template>
                </small>
              </button>
            </li>
          </ul>
          <p v-else class="pairing-empty">
            {{ files.length ? '当前筛选没有结果。' : '没有可挂的文件了——先到「项目文件」页上传。' }}
          </p>
        </div>
      </div>
    </template>
  </section>
</template>

<style scoped>
.pairing { margin-bottom: 16px; border: 1px solid var(--line); border-top: 3px solid var(--blue); background: var(--white); }
.pairing-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; padding: 14px 16px; border-bottom: 1px solid var(--line); }
.pairing-head span { color: var(--blue); font: 800 10px var(--display); letter-spacing: .12em; }
.pairing-head h3 { margin: 3px 0 5px; font-size: 16px; }
.pairing-head p { margin: 0; max-width: 62ch; color: var(--muted); font-size: 12px; line-height: 1.8; }
.pairing-done { flex-shrink: 0; padding: 3px 10px; border-radius: 999px; background: rgba(31, 154, 92, .12); color: var(--ok, #1f9a5c); font-size: 12px; }
.pairing-hint { margin: 0; padding: 16px; color: var(--muted); font-size: 13px; }
.pairing-error { color: var(--risk); }
.pairing-note { margin: 0; padding: 9px 16px; background: #fdf6ec; color: #7a4a10; font-size: 12px; line-height: 1.7; }

.pairing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1px; background: var(--line); }
.pairing-col { display: grid; gap: 8px; padding: 12px 14px; background: var(--white); align-content: start; }
.pairing-col > header { display: flex; align-items: baseline; justify-content: space-between; }
.pairing-col > header b { font-size: 13px; }
.pairing-col > header small { color: var(--muted); font-size: 12px; }
.pairing-col input { padding: 6px 10px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; }
.pairing-col ul { max-height: 420px; margin: 0; padding: 0; overflow: auto; list-style: none; display: grid; gap: 4px; }
.pairing-col li button {
  display: grid; gap: 2px; width: 100%; padding: 7px 10px;
  border: 1px solid transparent; border-radius: var(--radius-s);
  background: #f7f9fc; cursor: pointer; text-align: left;
}
.pairing-col li button:hover:not(:disabled) { border-color: var(--blue); background: rgba(0, 117, 174, .06); }
.pairing-col li button:disabled { opacity: .5; cursor: not-allowed; }
.pairing-col li button.picked { border-color: var(--blue); border-width: 2px; background: rgba(0, 117, 174, .1); }
.pairing-col li b { font-size: 13px; color: var(--ink); line-height: 1.4; }
.pairing-col li small { color: var(--muted); font-size: 11px; }
.pairing-target { margin: 0; color: var(--blue); font-size: 12px; }
.pairing-target-idle { color: var(--muted); }
.pairing-empty { margin: 0; color: var(--muted); font-size: 12px; }

@media (max-width: 900px) { .pairing-grid { grid-template-columns: 1fr; } }
</style>
